import {
  useIsS,
  useIsXS,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSIcon,
  WSText
} from "@wingspanhq/fe-component-library";
import classNames from "classnames";
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useBrowserPageTitle } from "../../hooks/useBrowserPageTitle";
import { AppFooter } from "../AppFooter";

import { Navigation } from "./Navigation";
import { Stepper } from "./Stepper";
import styles from "./index.module.scss";
import { FlowStep } from "../FlowSwitch/types";

export type LayoutProps = {
  title?: string;
  onClose?: () => void;
  onBack?: () => void;
  navigation?: FlowStep[];
  customHeader?: React.ReactNode;
  hideHeader?: boolean;
  wide?: boolean;
  showFooterLinks?: boolean;
  showFooterAdditionalInfo?: boolean;
};

export const Layout: React.FC<LayoutProps> = ({
  title,
  onClose,
  onBack,
  navigation,
  wide,
  customHeader,
  hideHeader,
  showFooterLinks = true,
  showFooterAdditionalInfo = false,
  children
}) => {
  useBrowserPageTitle(title);
  const isXS = useIsXS();
  const isS = useIsS();
  const { pathname } = useLocation();
  const layoutRef = useRef<HTMLDivElement>(null);

  const hasNavigation = useMemo(
    () => navigation && navigation.length > 0,
    [navigation]
  );

  // The layout element is the scroll container (position: fixed, overflow-y:
  // auto), so navigating between steps/pages leaves it scrolled wherever the
  // previous page ended. Reset it to the top on every route change.
  useEffect(() => {
    layoutRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  const topHeaderPadding =
    window.document
      .querySelector("[data-headerheight]")
      ?.getAttribute("data-headerheight") || "0";

  return (
    <WSElement
      ref={layoutRef}
      className={styles.layout}
      style={{ paddingTop: topHeaderPadding + "px" }}
    >
      {customHeader ? (
        customHeader
      ) : hideHeader ? null : (
        <WSFlexBox.CenterY className={styles.header} px="XL" py="L">
          {onBack ? (
            <WSIcon name="arrow-left" onClick={onBack} />
          ) : (
            <WSElement />
          )}

          <WSFlexBox.Center className={styles.headerCenter}>
            <WSText.ParagraphSm weight="medium">{title}</WSText.ParagraphSm>
          </WSFlexBox.Center>

          {onClose ? <WSIcon name="exit" onClick={onClose} /> : <WSElement />}
        </WSFlexBox.CenterY>
      )}

      <WSElement
        className={classNames(
          styles.content,
          hasNavigation && styles.twoColumn,
          wide && styles.threeColumn
        )}
      >
        {hasNavigation ? (
          <WSGrid>
            <WSGrid.Item span={{ m: "3" }}>
              {isXS || isS ? (
                <Stepper mb="L" navigation={navigation || []} />
              ) : (
                <Navigation navigation={navigation || []} />
              )}
            </WSGrid.Item>
            <WSGrid.Item span={{ m: "9" }}>{children}</WSGrid.Item>
          </WSGrid>
        ) : (
          children
        )}
      </WSElement>
      <AppFooter
        showLinks={showFooterLinks}
        showAdditionalInfo={showFooterAdditionalInfo}
      />
    </WSElement>
  );
};
